import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, detailFromError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";

const VERIFY_VARIANT = {
  on_orders: "default",
  needs_login: "destructive",
  soft_block: "destructive",
  unknown: "secondary",
};

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [cookieJson, setCookieJson] = useState("");
  // Phase 7 — controlled edit state for the "Your Amazon display name"
  // field. Initialised from settings.user_display_name_full ||
  // user_display_name_hint || "" as soon as settings load.
  const [nameDraft, setNameDraft] = useState("");

  const load = async () => {
    try {
      const res = await api.get("/settings");
      setSettings(res.data);
      // Seed draft on first load / after a verify captures the hint.
      const seed =
        res.data.user_display_name_full ||
        res.data.user_display_name_hint ||
        "";
      setNameDraft(seed);
    } catch (e) {
      toast.error(detailFromError(e));
    }
  };

  useEffect(() => { load(); }, []);

  const setAccountType = async (value) => {
    setBusy(true);
    try {
      const res = await api.put("/settings/account-type", { account_type: value });
      setSettings(res.data);
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setBusy(false);
    }
  };

  const submitImport = async () => {
    let parsed;
    try {
      parsed = JSON.parse(cookieJson);
    } catch {
      toast.error("Invalid JSON. Paste the full Cookie-Editor export.");
      return;
    }
    if (!Array.isArray(parsed)) {
      toast.error("Expected a JSON array of cookies.");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/settings/cookies", { cookies: parsed });
      toast.success(`${res.data.imported_count} cookies imported (${res.data.amazon_in_count} for amazon.in)`);
      setImportOpen(false);
      setCookieJson("");
      await load();
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const res = await api.post("/settings/verify");
      if (res.data.result === "on_orders") {
        toast.success("Session verified — logged in");
        if (res.data.display_name_hint) {
          toast.message(`Detected Amazon name: "${res.data.display_name_hint}"`);
        }
      } else {
        toast.error(`Verify: ${res.data.result}`);
      }
      await load();
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await api.post("/settings/clear");
      toast.success("Session cleared");
      await load();
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setBusy(false);
    }
  };

  const setGroupByPO = async (next) => {
    setBusy(true);
    try {
      const res = await api.put("/settings/group-by-po", { group_by_po: !!next });
      setSettings(res.data);
      toast.success(next ? "PDFs will be grouped by PO# in zip" : "PDFs will use flat layout in zip");
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveDisplayName = async () => {
    setBusy(true);
    try {
      const trimmed = nameDraft.trim();
      const res = await api.put("/settings/display-name", {
        user_display_name_full: trimmed || null,
      });
      setSettings(res.data);
      if (trimmed) {
        toast.success(`Saved: "${trimmed}" will be used to filter "orders I placed myself"`);
      } else {
        toast.success("Display name cleared — filter will use the auto-detected hint");
      }
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setBusy(false);
    }
  };

  const effectiveName = useMemo(() => {
    if (!settings) return "";
    return (
      settings.user_display_name_full ||
      settings.user_display_name_hint ||
      ""
    );
  }, [settings]);

  const nameIsCustom = !!(
    settings?.user_display_name_full &&
    settings.user_display_name_full !== settings.user_display_name_hint
  );

  if (!settings) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight" data-testid="settings-page-title">Settings</h1>

      <Card data-testid="cookie-session-card">
        <CardHeader>
          <CardTitle>Cookie Session</CardTitle>
          <CardDescription>
            The app authenticates as you using cookies exported from your own browser session.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3" data-testid="cookie-status-row">
            {settings.has_cookies ? (
              <>
                <Badge variant="default">Imported</Badge>
                <span className="text-sm text-muted-foreground" data-testid="cookie-imported-at">
                  on {new Date(settings.cookie_imported_at).toLocaleString()}
                </span>
                {settings.last_verify ? (
                  <Badge
                    variant={VERIFY_VARIANT[settings.last_verify.result] || "secondary"}
                    data-testid="last-verify-badge"
                    className="uppercase tracking-wider"
                  >
                    {settings.last_verify.result}
                  </Badge>
                ) : (
                  <Badge variant="secondary" data-testid="last-verify-badge">never verified</Badge>
                )}
                {settings.last_verify?.at && (
                  <span className="text-xs text-muted-foreground">
                    last verified {new Date(settings.last_verify.at).toLocaleString()}
                  </span>
                )}
              </>
            ) : (
              <Badge variant="destructive" data-testid="cookie-status-empty">No cookies imported</Badge>
            )}
          </div>
          {settings.cookie_sample_names?.length > 0 && (
            <div className="text-xs text-muted-foreground font-mono">
              sample cookie names: {settings.cookie_sample_names.join(", ")}
            </div>
          )}
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => setImportOpen(true)} disabled={busy} data-testid="open-import-button">
              {settings.has_cookies ? "Re-import cookies" : "Import cookies"}
            </Button>
            <Button variant="outline" onClick={verify} disabled={busy || !settings.has_cookies} data-testid="verify-session-button">
              Verify session
            </Button>
            <Button variant="ghost" onClick={clear} disabled={busy || !settings.has_cookies} data-testid="clear-session-button">
              Clear session
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="display-name-card">
        <CardHeader>
          <CardTitle>Your Amazon display name</CardTitle>
          <CardDescription>
            Used to filter <span className="font-medium text-foreground">&ldquo;Only orders I placed myself&rdquo;</span>{" "}
            on the Run page. We match this against the &ldquo;Placed by&rdquo; label on each Amazon order card.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="text-muted-foreground">Auto-detected from Amazon greeting:</span>
            {settings.user_display_name_hint ? (
              <Badge variant="secondary" data-testid="display-name-hint-badge" className="font-normal">
                &ldquo;{settings.user_display_name_hint}&rdquo;
              </Badge>
            ) : (
              <Badge variant="outline" data-testid="display-name-hint-badge" className="font-normal italic">
                Not detected yet — click Verify session above
              </Badge>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="display-name-input">
              Full name (as it appears on Amazon)
            </Label>
            <Input
              id="display-name-input"
              data-testid="display-name-input"
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              placeholder={settings.user_display_name_hint || "e.g. Sagar Shetty"}
              maxLength={120}
              disabled={busy}
              className="md:max-w-md"
            />
            <p className="text-xs text-muted-foreground">
              We first try an exact case-insensitive full-name match against the &ldquo;Placed by&rdquo; on
              each order. If that misses, we fall back to a first-word match — so
              &ldquo;{settings.user_display_name_hint || "Sagar"}&rdquo; will still match
              &ldquo;{settings.user_display_name_hint || "Sagar"} Shetty&rdquo;.
            </p>
            <p
              className="text-xs text-amber-700 dark:text-amber-400"
              data-testid="display-name-full-name-hint"
            >
              <strong>Tip:</strong> use the FULL name Amazon shows on the order card
              (e.g. &ldquo;Sagar Shetty&rdquo;, not just &ldquo;Sagar&rdquo;). If your account
              has two people sharing a first name, a short name will keep the other person&apos;s
              orders in your report by mistake.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={saveDisplayName}
              disabled={busy || nameDraft.trim() === (settings.user_display_name_full || "")}
              data-testid="save-display-name-button"
            >
              Save display name
            </Button>
            {nameIsCustom && (
              <span className="text-xs text-emerald-700 dark:text-emerald-400" data-testid="display-name-custom-hint">
                Using your custom value (overrides the auto-detected hint).
              </span>
            )}
            {!nameIsCustom && effectiveName && (
              <span className="text-xs text-muted-foreground" data-testid="display-name-effective-hint">
                Effective value for filtering: <span className="font-medium text-foreground">{effectiveName}</span>
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      <Card data-testid="output-card">
        <CardHeader>
          <CardTitle>Output</CardTitle>
          <CardDescription>How invoice PDFs are laid out inside the downloadable zip.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-3">
            <Switch
              id="group-by-po"
              data-testid="group-by-po-switch"
              checked={!!settings.group_by_po}
              onCheckedChange={setGroupByPO}
              disabled={busy}
            />
            <div className="space-y-1">
              <Label htmlFor="group-by-po" className="cursor-pointer">Group invoices by PO# in zip</Label>
              <p className="text-xs text-muted-foreground">
                {settings.group_by_po ? (
                  <>
                    <span className="font-medium text-foreground">ON — grouped:</span>{" "}
                    <code className="font-mono text-[11px]">pdfs/&lt;po_slug&gt;/&lt;order_id&gt;/2026-06-16__…__invoice-01.pdf</code>
                    {" — "}orders without a PO# go to <code className="font-mono text-[11px]">pdfs/_no_po/</code>.
                    Useful when you want physical separation between different work streams.
                  </>
                ) : (
                  <>
                    <span className="font-medium text-foreground">OFF (default) — flat:</span>{" "}
                    <code className="font-mono text-[11px]">pdfs/2026-06-16__405-9060491-6251551__invoice-01.pdf</code>.
                    Sort the folder alphabetically in any file explorer to see files chronologically, with same-order files together and numbered in sequence (invoice-01, invoice-02, credit-note-01, …). Accountant-friendly.
                  </>
                )}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="account-type-card">
        <CardHeader>
          <CardTitle>Account Type</CardTitle>
          <CardDescription>Which Amazon storefront to scrape.</CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={settings.account_type}
            onValueChange={setAccountType}
            className="space-y-3"
            data-testid="account-type-group"
          >
            <label className="flex items-center gap-3 cursor-pointer">
              <RadioGroupItem value="regular" id="acct-regular" data-testid="account-regular" />
              <span className="font-medium">Regular</span>
              <span className="text-sm text-muted-foreground">amazon.in</span>
            </label>
            <label className="flex items-center gap-3 cursor-not-allowed opacity-60">
              <RadioGroupItem value="business" id="acct-business" disabled data-testid="account-business" />
              <span className="font-medium">Business</span>
              <span className="text-sm text-muted-foreground">amazon.in/b2b</span>
              <Badge variant="secondary" data-testid="business-coming-soon">Coming soon</Badge>
            </label>
          </RadioGroup>
        </CardContent>
      </Card>

      <Card data-testid="help-card">
        <CardHeader>
          <CardTitle>How to export cookies</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside leading-relaxed">
            <li>Install the free <span className="font-medium text-foreground">Cookie-Editor</span> extension in Chrome / Firefox / Edge.</li>
            <li>Log into amazon.in normally in that browser.</li>
            <li>Open Cookie-Editor on any amazon.in tab → click <span className="font-medium text-foreground">Export</span> → <span className="font-medium text-foreground">Export as JSON</span>.</li>
            <li>Click <span className="font-medium text-foreground">Re-import cookies</span> above and paste the JSON.</li>
          </ol>
          <Separator className="my-4" />
          <p className="text-xs text-muted-foreground">
            Cookies are encrypted before storage. They can be revoked at any time by clicking Clear session,
            or by signing out of amazon.in in your normal browser.
          </p>
        </CardContent>
      </Card>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-2xl" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle>Import cookies</DialogTitle>
            <DialogDescription>Paste the JSON array exported from Cookie-Editor.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="cookies-textarea">Cookie JSON</Label>
            <Textarea
              id="cookies-textarea"
              data-testid="cookies-textarea"
              value={cookieJson}
              onChange={(e) => setCookieJson(e.target.value)}
              placeholder='[{"domain":".amazon.in","name":"session-id","value":"...", ...}, ...]'
              className="font-mono text-xs min-h-[200px]"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setImportOpen(false)} disabled={busy} data-testid="cancel-import-button">Cancel</Button>
            <Button onClick={submitImport} disabled={busy} data-testid="submit-import-button">Import</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="text-sm">
        Ready to scrape? <Link to="/run" className="text-primary underline underline-offset-4" data-testid="go-to-run-link">Go to Run →</Link>
      </div>
    </div>
  );
}
